import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import type { AdminServiceHealthStatus } from "@a7y/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  RefreshCw,
  XCircle,
} from "lucide-react";

// Known services with display metadata
const SERVICE_META: Record<string, { label: string; statusPageUrl: string }> = {
  anthropic: {
    label: "Anthropic / Claude",
    statusPageUrl: "https://status.claude.com",
  },
  openai: {
    label: "OpenAI",
    statusPageUrl: "https://status.openai.com",
  },
  perplexity: {
    label: "Perplexity",
    statusPageUrl: "https://status.perplexity.com",
  },
  stripe: {
    label: "Stripe",
    statusPageUrl: "https://www.stripestatus.com",
  },
  clerk: {
    label: "Clerk",
    statusPageUrl: "https://status.clerk.com",
  },
  resend: {
    label: "Resend",
    statusPageUrl: "https://resend-status.com",
  },
  decodo: {
    label: "Decodo",
    statusPageUrl: "https://status.decodo.com",
  },
  cloudflare: {
    label: "Cloudflare",
    statusPageUrl: "https://www.cloudflarestatus.com",
  },
  "google-cloud": {
    label: "Google Cloud",
    statusPageUrl: "https://status.cloud.google.com",
  },
  firebase: {
    label: "Firebase",
    statusPageUrl: "https://status.firebase.google.com",
  },
};

type Severity = AdminServiceHealthStatus["severity"];

function severityConfig(severity: Severity): {
  icon: React.ReactNode;
  badgeClass: string;
  cardBorderClass: string;
  label: string;
} {
  switch (severity) {
    case "critical":
      return {
        icon: <XCircle className="h-5 w-5 text-red-500" />,
        badgeClass: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
        cardBorderClass: "border-red-300 dark:border-red-700",
        label: "Critical",
      };
    case "major":
      return {
        icon: <AlertCircle className="h-5 w-5 text-orange-500" />,
        badgeClass: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
        cardBorderClass: "border-orange-300 dark:border-orange-700",
        label: "Major",
      };
    case "minor":
      return {
        icon: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
        badgeClass: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
        cardBorderClass: "border-yellow-300 dark:border-yellow-700",
        label: "Minor",
      };
    case "none":
      return {
        icon: <CheckCircle className="h-5 w-5 text-green-500" />,
        badgeClass: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
        cardBorderClass: "border-green-300 dark:border-green-700",
        label: "Operational",
      };
    default:
      return {
        icon: <Activity className="h-5 w-5 text-zinc-400" />,
        badgeClass: "bg-zinc-100 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300",
        cardBorderClass: "border-zinc-200 dark:border-zinc-700",
        label: "No Data",
      };
  }
}

function ServiceCard({ entry }: { entry: AdminServiceHealthStatus }) {
  const meta = SERVICE_META[entry.service] ?? {
    label: entry.service.charAt(0).toUpperCase() + entry.service.slice(1),
    statusPageUrl: "#",
  };

  // Override severity to "none" for resolved/postmortem incidents, regardless of stored severity
  const resolvedStatuses = ["resolved", "postmortem", "complete", "completed"];
  const effectiveSeverity: Severity =
    entry.status && resolvedStatuses.includes(entry.status.toLowerCase())
      ? "none"
      : entry.severity;

  const { icon, badgeClass, cardBorderClass, label } = severityConfig(effectiveSeverity);

  return (
    <div
      className={`rounded-xl border bg-white dark:bg-zinc-800/60 p-5 shadow-sm ${cardBorderClass}`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <span className="font-semibold text-zinc-900 dark:text-white text-base truncate">
            {meta.label}
          </span>
        </div>
        <a
          href={entry.url ?? meta.statusPageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
        >
          Status page
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {/* Severity badge + status string */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeClass}`}>
          {label}
        </span>
        {entry.status && (
          <span className="text-sm text-zinc-500 dark:text-zinc-400 capitalize">
            {entry.status}
          </span>
        )}
      </div>

      {/* Latest event */}
      {entry.title && (
        <p className="mt-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 line-clamp-2">
          {entry.title}
        </p>
      )}
      {entry.description && (
        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400 line-clamp-2">
          {entry.description}
        </p>
      )}

      {/* Timestamp */}
      <p className="mt-3 text-xs text-zinc-400">
        {entry.timestamp
          ? `Last event: ${formatDate(entry.timestamp)}`
          : "No events recorded"}
      </p>
    </div>
  );
}

export function ServiceHealthPage() {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["service-health"],
    queryFn: () => api.getServiceHealth(),
    refetchInterval: 5 * 60 * 1000, // auto-refresh every 5 minutes
  });

  // On a hard page refresh, pull fresh data directly from ATOM feeds
  useEffect(() => {
    const navEntry = performance.getEntriesByType(
      "navigation"
    )[0] as PerformanceNavigationTiming | undefined;
    if (navEntry?.type === "reload") {
      api.refreshServiceHealth().then((fresh) => {
        queryClient.setQueryData(["service-health"], fresh);
      }).catch(() => {
        // Live refresh failed — cached Firestore data remains
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual refresh: fetch live from ATOM feeds (same as page reload)
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      const fresh = await api.refreshServiceHealth();
      queryClient.setQueryData(["service-health"], fresh);
    } catch {
      // Live refresh failed — fall back to re-querying Firestore
      refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const entries = data?.data ?? [];

  // Compute effective severity per entry (override resolved/postmortem to "none")
  const resolvedStatuses = ["resolved", "postmortem", "complete", "completed"];
  const getEffectiveSeverity = (e: AdminServiceHealthStatus): Severity =>
    e.status && resolvedStatuses.includes(e.status.toLowerCase()) ? "none" : e.severity;

  // Compute overall status using effective severity
  const hasCritical = entries.some((e) => getEffectiveSeverity(e) === "critical");
  const hasMajor = entries.some((e) => getEffectiveSeverity(e) === "major");
  const hasMinor = entries.some((e) => getEffectiveSeverity(e) === "minor");
  const overallSeverity: Severity = hasCritical
    ? "critical"
    : hasMajor
    ? "major"
    : hasMinor
    ? "minor"
    : entries.length > 0
    ? "none"
    : null;
  const { icon: overallIcon, badgeClass: overallBadgeClass, label: overallLabel } =
    severityConfig(overallSeverity);

  return (
    <div>
      <PageHeader
        title="Service Health"
        subtitle="Latest status for all external dependency services"
      />

      {/* Overall status summary */}
      {!isLoading && entries.length > 0 && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/40 px-5 py-4">
          {overallIcon}
          <div>
            <p className="text-sm font-semibold text-zinc-900 dark:text-white">
              Overall: <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${overallBadgeClass}`}>{overallLabel}</span>
            </p>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
              Monitoring {entries.length} external services
            </p>
          </div>
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="ml-auto flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 py-1.5 text-sm text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-600 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-40 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/60 animate-pulse"
            />
          ))}
        </div>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <div className="rounded-xl border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-6 text-center">
          <XCircle className="mx-auto h-10 w-10 text-red-400" />
          <p className="mt-2 font-medium text-red-700 dark:text-red-400">
            Failed to load service health data
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Service cards */}
      {!isLoading && !isError && (
        <div className="grid grid-cols-2 gap-4">
          {entries.map((entry) => (
            <ServiceCard key={entry.service} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}
